import React from "react";
import { IGlobalIndexData } from "../../interfaces/StockApi.interface";
import {
  getGlobalIndicesBySymbol as getGlobalIndicesBySymbolService,
  getMarketHeadingVariant,
} from "../../services/TradingMallService";
import {
  getMarketInsightsHeadingCopy,
} from "../MarketInsigtsHeading/marketInsightsVariant";
import PreMarketInsightsClient, {
  PreMarketInsightCardDisplay,
} from "./PreMarketInsightsClient";

export interface PreMarketInsightCardItem {
  title: string;
  type: string;
  openInNewTab: string;
  image: string;
  ctaInNewTab: string;
  imageAlt: string;
}

export interface PreMarketInsightsContent {
  componentPosition?: string;
  section?: {
    title: string;
    preMarketTitle?: string;
    postMarketTitle?: string;
    weekendTitle?: string;
    openInNewTab?: boolean | string;
    label: string;
    src: string;
  };
  indexCard?: {
    label: string;
  };
  insightCards?: Array<Record<string, PreMarketInsightCardItem>>;
}

export interface PreMarketInsightsProps {
  content?: PreMarketInsightsContent;
}

function flattenInsightCardItems(
  insightCards: PreMarketInsightsContent["insightCards"],
): PreMarketInsightCardItem[] {
  const group = insightCards?.[0];
  if (!group) return [];
  return Object.keys(group)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .map((key) => group[key])
    .filter((item): item is PreMarketInsightCardItem => Boolean(item?.title));
}

function toDisplayCards(
  items: PreMarketInsightCardItem[],
): PreMarketInsightCardDisplay[] {
  return items.map((item) => ({
    title: item.title,
    image: item.image,
    imageAlt: item.imageAlt,
  }));
}

const PreMarketInsights = async ({ content }: PreMarketInsightsProps) => {
  const { section, indexCard } = content ?? {};
  const insightCardItems = flattenInsightCardItems(content?.insightCards);
  const defaultTitle = "Pre-market insights";
  const resolvedSectionTitle = section?.title ?? defaultTitle;
  const resolvedHeadingLabel = section?.label ?? "View all";
  const resolvedHeadingSrc = section?.src ?? "";
  const resolvedIndexLabel = indexCard?.label ?? "GIFT NIFTY";
  const variant = await getMarketHeadingVariant();
  const { title: headingTitle } = getMarketInsightsHeadingCopy(
    {
      preMarketTitle: section?.preMarketTitle ?? resolvedSectionTitle,
      postMarketTitle: section?.postMarketTitle ?? resolvedSectionTitle,
      weekendTitle: section?.weekendTitle ?? resolvedSectionTitle,
    },
    variant,
  );

  const response = await getGlobalIndicesBySymbolService();
  const apiData: IGlobalIndexData | undefined = response?.data;

  const ltp = Number(apiData?.cmp) || 0;
  const chg = Number(apiData?.chg) || 0;
  const chgPer = Number(apiData?.chgPer) || 0;
  const isPositive = chg >= 0;
  const isLtpPositive = chg >= 0;

  let performanceIcon;
  if (isLtpPositive) {
    performanceIcon = (
      <i className="icomoon inline-flex h-[12px] w-[12px] items-center justify-center overflow-hidden leading-none text-[#067161] before:inline-block before:h-[12px] before:w-[12px] before:leading-[12px] before:text-[12px] before:content-['\e980']" />
    );
  } else {
    performanceIcon = (
      <i className="icomoon inline-flex h-[12px] w-[12px] items-center justify-center overflow-hidden leading-none text-[#B40000] before:inline-block before:h-[12px] before:w-[12px] before:leading-[12px] before:text-[12px] before:content-['\e981']" />
    );
  }

  return (
    <section className="flex flex-col gap-[12px] p-[16px]">
      <h2 className="text-[18px] font-medium text-[#1a1a1a] md:text-[20px]">
        {headingTitle}
      </h2>

      <div className="flex max-w-[550px] flex-col gap-[12px]">
        <div className="flex items-center justify-between rounded-[4px] border border-[#E5E5E5] bg-[#E5E5E5] p-[12px]">
          <div className="text-[16px] font-[400px] text-[#1a1a1a]">
            {resolvedIndexLabel}
          </div>
          <div className="flex justify-between items-center gap-[4px]">
            <div
              className={`font-[400px] md:text-[16px] text-[14px] ${isLtpPositive ? "text-green-500" : "text-[#ff1f00]"}`}
            >
              <span className="inline-flex items-center gap-[4px]">
              {" "}
              {ltp.toFixed(2)} {performanceIcon}{" "}
              </span>
            </div>
            <div className="md:text-[14px] text-[12px] font-[400px] text-[#666666]">
              {" "}
              {isPositive ? "+" : "-"} {Math.abs(chg).toFixed(2)} (
              {isPositive ? "+" : "-"} {Math.abs(chgPer).toFixed(2)}%)
            </div>
          </div>
        </div>

        <PreMarketInsightsClient
          label={resolvedHeadingLabel}
          src={resolvedHeadingSrc}
          insightCards={toDisplayCards(insightCardItems)}
          openInNewTab={content?.section?.openInNewTab}
        />
      </div>
    </section>
  );
};

export default PreMarketInsights;
